import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import LogOutWarningDialog from './LogOutWarningDialog';
import {
    getSecondsBeforeSessionExpires,
    postSessionKeepAlive,
    getExpirationTimestampFromCookie,
    clearKeepAliveRequestPending,
    keepAliveRequestIsPending,
    setKeepAliveRequestPending,
} from './session';
import sleep from './sleep';
import ResponseStatus from '../../constants/ResponseStatus';
import LoadingScreenContext from '../../services/LoadingScreenContext';
import logout from '../../services/logout';

const SLEEP_BETWEEN_KEEP_ALIVE_ATTEMPTS = 50;
const DEFAULT_SECONDS_BEFORE_WARNING_DIALOG_APPEARS = 60;
const MAX_KEEP_ALIVE_ATTEMPTS = 5;

const submitLogoutForm = () => {
    clearKeepAliveRequestPending();
    logout();
};

const handleLogOut = () => {
    // Continue may have been called before an automatic log out, ignore until continue completes.
    if (!keepAliveRequestIsPending()) {
        submitLogoutForm();
    }
};

function IdleTracker({
    secondsBeforeWarningDialogAppears = DEFAULT_SECONDS_BEFORE_WARNING_DIALOG_APPEARS,
}) {
    const { setLoading } = useContext(LoadingScreenContext);

    const [secondsRemaining, setSecondsRemaining] = useState(
        getSecondsBeforeSessionExpires(getExpirationTimestampFromCookie()),
    );

    const keepSessionAlive = async (attempts = 1) => {
        // Set pending so that auto-logout doesn't work if user clicks continue
        setLoading(true);
        setKeepAliveRequestPending();
        const response = await postSessionKeepAlive();
        switch (response.responseStatus) {
            case ResponseStatus.SUCCESS: {
                setLoading(false);
                const timestamp = getExpirationTimestampFromCookie();
                setSecondsRemaining(getSecondsBeforeSessionExpires(timestamp));

                // Extending session was successful, so clear pending
                clearKeepAliveRequestPending();
                break;
            }
            case ResponseStatus.FAILURE: {
                setLoading(false);
                submitLogoutForm();
                break;
            }
            case ResponseStatus.UI_NETWORK_ERROR: {
                // Technical failure from server = retry up to 5 times
                if (attempts === MAX_KEEP_ALIVE_ATTEMPTS) {
                    setLoading(false);
                    submitLogoutForm();
                } else {
                    await sleep(SLEEP_BETWEEN_KEEP_ALIVE_ATTEMPTS);
                    await keepSessionAlive(attempts + 1);
                }
                break;
            }
            default:
            // No-op
        }
    };

    const handleContinue = async () => {
        await keepSessionAlive();
    };

    useEffect(() => {
        if (secondsRemaining <= 0) {
            handleLogOut();
        }
        const timeout = setTimeout(() => {
            const calculatedMsRemaining = getSecondsBeforeSessionExpires(
                getExpirationTimestampFromCookie(),
            );
            if (secondsRemaining > 0) {
                setSecondsRemaining(calculatedMsRemaining);
            }
        }, 100);
        return () => {
            clearTimeout(timeout);
            clearKeepAliveRequestPending();
        };
    }, [secondsRemaining]);

    if (secondsRemaining <= secondsBeforeWarningDialogAppears) {
        return (
            <LogOutWarningDialog
                onContinue={handleContinue}
                onLogOut={handleLogOut}
                secondsRemaining={secondsRemaining}
            />
        );
    }

    return null;
}

IdleTracker.propTypes = {
    secondsBeforeWarningDialogAppears: PropTypes.number,
};

export default IdleTracker;
